import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../components-v2/Theme/theme';
import { background, charmGray, grayIconFill, header, mention, titles } from '../../../styles/colors';
import { CssSelectors } from '../../../styles/styles';

// Keep the textarea and highlight layer text metrics identical.
const COMPOSER_TEXT_BORDER_WIDTH = 1;
const COMPOSER_TEXT_FONT_SIZE = THEME.typography.fontSizes.sm;
const COMPOSER_TEXT_PADDING_TOP = 10;
const COMPOSER_TEXT_PADDING_X = 14;
const COMPOSER_TEXT_PADDING_BOTTOM = 14;

// Matches the textarea's global input style.
const COMPOSER_TEXT_FONT_FAMILY = '"Sora", sans-serif';

export const styleSheet = StyleSheet.create({
	charCount: {
		color: header,
		display: 'flex',
		fontSize: 12,
		justifyContent: 'flex-end',
		marginRight: 10,
		...CssSelectors.allDescendants(
			{
				display: 'flex',
				paddingRight: 6,
			},
			'button'
		),
	},
	composerContainer: {
		marginTop: 10,
	},
	container: {
		color: grayIconFill,
		fontSize: 14,
		marginBottom: 72,
	},
	header: {
		color: titles,
		fontSize: 18,
	},
	headerContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: 20,
	},
	labelContainer: {
		alignItems: 'flex-end',
		color: charmGray,
		display: 'flex',
		fontSize: 14,
		justifyContent: 'space-between',
		paddingBottom: 10,
	},
	labelEmoji: {
		alignItems: 'center',
		display: 'flex',
	},
	mediaContainer: {
		color: charmGray,
		fontSize: 14,
		marginTop: 48,
	},
	mentionAnchor: {
		height: 1,
		pointerEvents: 'none',
		position: 'absolute',
		width: 1,
		zIndex: 2,
	},
	mentionDropdown: {
		...THEME.common.scrollBar,
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: '0 8px 24px 0 rgba(13, 15, 17, 0.12)',
		boxSizing: 'border-box',
		fontFamily: THEME.typography.fontFamilies.lexend,
		maxHeight: 280,
		minWidth: 320,
		overflowY: 'auto',
		padding: THEME.spacing.xs,
	},
	mentionEmptyState: {
		color: THEME.colors.text.light,
		fontFamily: THEME.typography.fontFamilies.lexend,
		fontSize: THEME.typography.fontSizes.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	// Box shadow adds fill without changing text layout.
	mentionHighlight: {
		background: mention,
		borderRadius: 4,
		boxShadow: `0 0 0 2px ${mention}`,
	},
	mentionHighlightUnconfirmed: {
		background: THEME.colors.warning.light,
		boxShadow: `0 0 0 2px ${THEME.colors.warning.light}`,
	},
	captionCount: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		paddingRight: THEME.spacing.sm,
	},
	captionCountOverLimit: {
		color: THEME.colors.warning.main,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	mentionHighlightLayer: {
		backgroundColor: background,
		borderColor: 'transparent',
		borderRadius: 6,
		borderStyle: 'solid',
		borderWidth: COMPOSER_TEXT_BORDER_WIDTH,
		bottom: 0,
		boxSizing: 'border-box',
		color: 'transparent',
		fontFamily: COMPOSER_TEXT_FONT_FAMILY,
		fontSize: COMPOSER_TEXT_FONT_SIZE,
		fontStyle: 'normal',
		fontWeight: 'normal',
		left: 0,
		letterSpacing: 'normal',
		lineHeight: 'normal',
		overflow: 'hidden',
		paddingBottom: COMPOSER_TEXT_PADDING_BOTTOM,
		paddingLeft: COMPOSER_TEXT_PADDING_X,
		paddingRight: COMPOSER_TEXT_PADDING_X,
		paddingTop: COMPOSER_TEXT_PADDING_TOP,
		pointerEvents: 'none',
		position: 'absolute',
		right: 0,
		textAlign: 'left',
		textIndent: 0,
		top: 0,
		whiteSpace: 'pre-wrap',
		wordSpacing: 'normal',
		wordWrap: 'break-word',
	},
	mentionHighlightLayerDisabled: {
		opacity: 0.5,
	},
	mentionBubble: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: '0 8px 24px 0 rgba(13, 15, 17, 0.12)',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		fontFamily: THEME.typography.fontFamilies.lexend,
		gap: THEME.spacing.xs / 2,
		maxWidth: 320,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		position: 'absolute',
		zIndex: 3,
	},
	mentionDisclaimer: {
		color: THEME.colors.text.light,
		fontFamily: THEME.typography.fontFamilies.lexend,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.4,
		paddingTop: THEME.spacing.sm,
	},
	mentionBubbleNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		lineHeight: 1.4,
	},
	mentionBubbleLink: {
		color: THEME.colors.primary.main,
		fontSize: THEME.typography.fontSizes.xs,
		textDecoration: 'underline',
		width: 'fit-content',
	},
	mentionResultItem: {
		alignItems: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: THEME.colors.text.main,
		cursor: 'pointer',
		display: 'flex',
		flex: '1 1 auto',
		flexDirection: 'column',
		gap: THEME.spacing.xs / 2,
		minWidth: 0,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		textAlign: 'left',
	},
	mentionResultLink: {
		color: THEME.colors.primary.main,
		flex: '0 0 auto',
		fontSize: THEME.typography.fontSizes.xs,
		padding: `0 ${THEME.spacing.md}px`,
		textDecoration: 'underline',
	},
	mentionResultMeta: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	mentionResultName: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	mentionResultNameRow: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		width: '100%',
	},
	mentionResultRow: {
		alignItems: 'center',
		borderRadius: THEME.common.borderRadius - 2,
		display: 'flex',
		justifyContent: 'space-between',
		transition: 'background-color 0.1s ease-in-out',
		':hover': {
			background: THEME.colors.hover.light,
		},
	},
	mentionResultRowHighlighted: {
		background: THEME.colors.hover.light,
	},
	mentionSectionTitle: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		letterSpacing: 0.4,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px ${THEME.spacing.xs}px`,
		textTransform: 'uppercase',
	},
	modal: {
		...CssSelectors.allDescendants(
			{
				padding: 0,
			},
			'.modal-overlay-content'
		),
	},
	textAreaContainer: {
		fieldSizing: 'content',
		fontFamily: COMPOSER_TEXT_FONT_FAMILY,
		fontSize: COMPOSER_TEXT_FONT_SIZE,
		minHeight: 124,
		paddingBottom: COMPOSER_TEXT_PADDING_BOTTOM,
		paddingLeft: COMPOSER_TEXT_PADDING_X,
		paddingRight: COMPOSER_TEXT_PADDING_X,
		paddingTop: COMPOSER_TEXT_PADDING_TOP,
	},
	textAreaOverHighlights: {
		background: 'transparent',
		position: 'relative',
		zIndex: 1,
	},
	textAreaWrapper: {
		position: 'relative',
	},
});
